import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords,
  type = 'website',
  image,
  location,
  structuredData
}) => {
  // Global brand keywords
  const globalKeywords = [
    'George Dives',
    'dive instructor',
    'scuba diving',
    'PADI certification',
    'PADI courses',
    'diving adventure',
    'dive travel',
    'travel adventure',
    'underwater exploration',
    'learn to dive',
    'dive packages',
    'diving experiences',
    'dive destinations',
    'professional diving',
    'Open Water',
    'Advanced Open Water',
    'Sidemount diving'
  ];

  // Combine with page-specific keywords
  const allKeywords = keywords ? [...globalKeywords, ...keywords] : globalKeywords;

  // Add location-specific keywords if provided
  if (location) {
    allKeywords.push(
      `diving in ${location.country}`,
      `scuba diving ${location.location}`,
      `dive center ${location.location}`,
      `PADI courses ${location.country}`
    );
  }

  const siteUrl = 'https://georgedives.com';
  const defaultImage = image || `${siteUrl}/og-image.jpg`;
  
  const defaultTitle = 'George Dives - Professional Dive Instructor & PADI Courses Worldwide';
  const defaultDescription = 'Join George for unforgettable diving adventures around the world. PADI certified courses, fun diving, and dive travel packages. From beginner to advanced - explore the underwater world with an experienced instructor.';
  
  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  
  // Build clean canonical URL (without query params, without trailing slash except for root)
  const getCanonicalUrl = () => {
    const path = window.location.pathname;
    // Remove trailing slash except for homepage
    const cleanPath = path === '/' ? '/' : path.replace(/\/$/, '');
    return `${siteUrl}${cleanPath}`;
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={allKeywords.join(', ')} />
      <meta name="author" content="George Dives" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Canonical URL - always points to production domain */}
      <link rel="canonical" href={getCanonicalUrl()} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={getCanonicalUrl()} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={defaultImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="George Dives" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={getCanonicalUrl()} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content={defaultImage} />

      {/* Instagram Integration */}
      <meta property="instagram:username" content="@george_dives" />

      {/* Mobile Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#0ea5e9" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
